import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Building2, ChevronLeft, ChevronRight, MapPin, Plus, RefreshCcw, Satellite, Search, ShieldCheck, Wifi } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRouters } from "../context/RouterContext";
import { FupUnitForm } from "../components/FupUnitForm";
import { FupUnitRow } from "../components/FupUnitRow";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";

const EMPTY = { router_id: "", label: "", interface: "", quota_gb: 4000, cycle_day: 1, note: "", sn_kit: "", email: "", starlink_data_id: "" };
const PER_PAGE = 9;
const RANK = { fup: 0, warning: 1, ok: 2 };

const Stat = ({ icon: Icon, label, value, tone, testid }) => (
  <div className="rounded-xl border border-border bg-card px-4 py-3 flex items-center gap-3" data-testid={testid}>
    <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${tone}`}><Icon className="w-5 h-5" /></div>
    <div><div className="text-xs text-muted-foreground">{label}</div><div className="text-2xl font-bold font-heading leading-tight">{value}</div></div>
  </div>
);

export default function StarlinkFup() {
  const { user } = useAuth();
  const { routers } = useRouters();
  const isAdmin = ["admin", "admin_fasnet"].includes(user?.role);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [ifaces, setIfaces] = useState([]);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dataStarlink, setDataStarlink] = useState([]);
  const [q, setQ] = useState("");
  const [filterRid, setFilterRid] = useState("ALL");
  const [filterSt, setFilterSt] = useState("ALL");
  const [sort, setSort] = useState("name");
  const [page, setPage] = useState(0);

  const load = () => api.get("/starlink-fup/units").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    if (!isAdmin) return;
    load();
    api.get("/starlink-fup/data-starlink").then((r) => setDataStarlink(r.data)).catch(() => {});
    const t = setInterval(() => { if (!document.hidden) load(); }, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const loadIfaces = (rid) => {
    setIfaces([]);
    if (!rid) return;
    api.get(`/starlink-fup/interfaces/${rid}`).then((r) => setIfaces(r.data)).catch((e) => toast.error(`Interface: ${errMsg(e)}`));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (form.editing) {
        await api.put(`/starlink-fup/units/${form.editing}`, { label: form.label, interface: form.interface, quota_gb: Number(form.quota_gb), cycle_day: Number(form.cycle_day), note: form.note, sn_kit: form.sn_kit || "", email: form.email || "", starlink_data_id: form.starlink_data_id || "", fup_hit_manual: form.fup_hit_manual ?? "", manual_used_gb: Number(form.manual_used_gb || 0) });
        toast.success("Unit Starlink diperbarui");
      } else {
        await api.post("/starlink-fup/units", { ...form, quota_gb: Number(form.quota_gb), cycle_day: Number(form.cycle_day) });
        toast.success("Unit Starlink ditambahkan");
      }
      setForm(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/starlink-fup/refresh");
      toast.info("Pembacaan dimulai di latar belakang…");
      const t0 = Date.now();
      const tick = async () => {
        const { data } = await api.get("/starlink-fup/units");
        setData(data);
        if (data.last_poll?.status === "running" && Date.now() - t0 < 180000) setTimeout(tick, 3000);
        else { setRefreshing(false); if (data.last_poll?.status === "done") toast.success(`Dibaca: ${data.last_poll.ok || 0} unit OK, ${data.last_poll.error || 0} gagal`); }
      };
      setTimeout(tick, 2000);
    } catch (e) { toast.error(errMsg(e)); setRefreshing(false); }
  };
  const rolling = async (u) => {
    if (!window.confirm(`Tandai ${u.label} (${u.router_name}) sudah diganti/rolling? Pemakaian ${u.used_gb} GB disimpan ke riwayat dan hitungan mulai dari 0.`)) return;
    try { await api.post(`/starlink-fup/units/${u.id}/rolling`); toast.success("Rolling dicatat"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (u) => {
    if (!window.confirm(`Hapus unit ${u.label} dari ${u.router_name}?`)) return;
    try { await api.delete(`/starlink-fup/units/${u.id}`); toast.success("Unit dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const groups = useMemo(() => {
    const kw = q.trim().toLowerCase();
    const m = new Map();
    (data?.units || []).forEach((u) => {
      if (filterRid !== "ALL" && u.router_id !== filterRid) return;
      if (filterSt !== "ALL" && u.status !== filterSt) return;
      if (kw && ![u.router_name, u.label, u.sn_kit, u.email, u.location].some((x) => (x || "").toLowerCase().includes(kw))) return;
      if (!m.has(u.router_id)) m.set(u.router_id, { id: u.router_id, name: u.router_name, location: u.location, units: [] });
      m.get(u.router_id).units.push(u);
    });
    const arr = [...m.values()];
    const worst = (g) => Math.min(...g.units.map((u) => RANK[u.status]));
    const maxPct = (g) => Math.max(...g.units.map((u) => u.pct));
    if (sort === "status") arr.sort((a, b) => worst(a) - worst(b) || maxPct(b) - maxPct(a) || a.name.localeCompare(b.name));
    else if (sort === "usage") arr.sort((a, b) => maxPct(b) - maxPct(a) || a.name.localeCompare(b.name));
    else arr.sort((a, b) => a.name.localeCompare(b.name));
    return arr;
  }, [data, q, filterRid, filterSt, sort]);
  const pages = Math.max(1, Math.ceil(groups.length / PER_PAGE));
  const cur = Math.min(page, pages - 1);
  const pageGroups = groups.slice(cur * PER_PAGE, cur * PER_PAGE + PER_PAGE);
  const routerOpts = useMemo(() => [...new Map((data?.units || []).map((u) => [u.router_id, u.router_name])).entries()].sort((a, b) => a[1].localeCompare(b[1])), [data]);
  const unitCountFor = (rid) => (data?.units || []).filter((u) => u.router_id === rid).length;
  const lp = data?.last_poll || {};
  const sel = "h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring";

  if (!isAdmin) return <Card className="p-8 text-center text-muted-foreground" data-testid="starlink-fup-forbidden"><ShieldCheck className="w-8 h-8 mx-auto mb-2" />Khusus Admin</Card>;

  return (
    <div className="space-y-5" data-testid="starlink-fup-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Satellite className="w-6 h-6 text-[#F04E23]" /> Multi-Starlink FUP Monitor</h1>
          <p className="text-sm text-muted-foreground mt-1">Pemakaian tiap unit Starlink per cabang (1–6 unit). Peringatan ganti/rolling saat menyentuh kuota FUP (default 4.000 GB).</p>
          <p className="text-[11px] font-mono text-muted-foreground mt-1" data-testid="fup-last-poll">
            {lp.finished_at ? <>Terakhir diperbarui: {fmtDateTime(lp.finished_at)} WIB • {lp.ok || 0} OK, {lp.error || 0} gagal</> : "Belum ada pembacaan"}
            {" "}• GB &amp; kecepatan otomatis tiap {Math.round((data?.poll_interval || 300) / 60)} menit (realtime) • Backup data FUP tiap 6 jam
            {lp.status === "running" && <span className="text-sky-400"> • sedang membaca…</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={refresh} disabled={refreshing} data-testid="fup-refresh"><RefreshCcw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />{refreshing ? "Membaca..." : "Baca Sekarang"}</Button>
          <Button className="bg-[#F04E23] hover:bg-[#d84420]" onClick={() => { setForm({ ...EMPTY, router_id: filterRid !== "ALL" ? filterRid : "" }); if (filterRid !== "ALL") loadIfaces(filterRid); }} data-testid="fup-add"><Plus className="w-4 h-4 mr-1.5" /> Tambah Starlink</Button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 xl:grid-cols-[repeat(4,minmax(0,1fr))_2fr] gap-3" data-testid="fup-summary">
          <Stat icon={Building2} label="Total Cabang" value={data.summary.routers} tone="bg-sky-500/15 text-sky-400" testid="fup-count-routers" />
          <Stat icon={Wifi} label="Total Unit Starlink" value={data.summary.total} tone="bg-emerald-500/15 text-emerald-400" testid="fup-count-total" />
          <Stat icon={AlertTriangle} label="Kena FUP" value={data.summary.fup} tone="bg-rose-500/15 text-rose-400" testid="fup-count-fup" />
          <Stat icon={AlertTriangle} label="Mendekati FUP" value={data.summary.warning} tone="bg-amber-500/15 text-amber-400" testid="fup-count-warning" />
          <div className="col-span-2 xl:col-span-1 space-y-2">
            <div className="relative"><Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" /><Input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Cari nama cabang, lokasi, SN KIT atau email Starlink..." className="pl-9" data-testid="fup-search" /></div>
            <div className="grid grid-cols-3 gap-2">
              <select value={filterRid} onChange={(e) => { setFilterRid(e.target.value); setPage(0); }} className={sel} data-testid="fup-filter-router"><option value="ALL">Semua Cabang</option>{routerOpts.map(([id, n]) => <option key={id} value={id}>{n}</option>)}</select>
              <select value={filterSt} onChange={(e) => { setFilterSt(e.target.value); setPage(0); }} className={sel} data-testid="fup-filter-status"><option value="ALL">Semua Status</option><option value="fup">Kena FUP</option><option value="warning">Mendekati FUP</option><option value="ok">Normal</option></select>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className={sel} data-testid="fup-sort"><option value="name">Urutkan: Nama A-Z</option><option value="status">Urutkan: Status FUP</option><option value="usage">Urutkan: Pemakaian</option></select>
            </div>
          </div>
        </div>
      )}

      {data && !groups.length && <Card className="p-10 text-center text-muted-foreground text-sm" data-testid="fup-empty">{data.units.length ? "Tidak ada cabang yang cocok dengan filter." : "Belum ada unit Starlink. Klik \"Tambah Starlink\" lalu pilih cabang dan interface router yang terhubung ke Starlink tersebut."}</Card>}

      <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
        {pageGroups.map((g) => (
          <Card key={g.id} className="bg-card border-border shadow-sm p-3 space-y-2" data-testid={`fup-group-${g.name}`}>
            <div className="flex items-start justify-between gap-2 px-1">
              <div className="min-w-0">
                <div className="font-heading font-bold text-base flex items-center gap-2 truncate"><Building2 className="w-4 h-4 text-[#F04E23] shrink-0" /><span className="truncate">{g.name}</span><span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-accent border border-border shrink-0">{g.units.length} unit</span></div>
                {g.location && <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{g.location}</div>}
              </div>
              {g.units.some((u) => u.status === "fup") && <span className="text-[10px] font-bold text-rose-400 flex items-center gap-1 shrink-0"><AlertTriangle className="w-3 h-3" /> Ganti / rolling</span>}
            </div>
            <div className="space-y-2">{g.units.map((u) => <FupUnitRow key={u.id} u={u} onRolling={rolling} onEdit={(x) => { setForm({ ...x, editing: x.id }); loadIfaces(x.router_id); }} onDelete={del} />)}</div>
          </Card>
        ))}
      </div>

      {groups.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-2 text-xs text-muted-foreground" data-testid="fup-pagination">
          <span>Menampilkan {cur * PER_PAGE + 1}–{Math.min(groups.length, (cur + 1) * PER_PAGE)} dari {groups.length} cabang</span>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="outline" className="h-8 w-8" disabled={cur === 0} onClick={() => setPage(cur - 1)} data-testid="fup-prev"><ChevronLeft className="w-4 h-4" /></Button>
            {Array.from({ length: pages }, (_, i) => i).filter((i) => Math.abs(i - cur) <= 2 || i === 0 || i === pages - 1).map((i) => (
              <Button key={i} size="sm" variant={i === cur ? "default" : "outline"} className={`h-8 min-w-8 ${i === cur ? "bg-[#F04E23] hover:bg-[#d84420]" : ""}`} onClick={() => setPage(i)} data-testid={`fup-page-${i + 1}`}>{i + 1}</Button>
            ))}
            <Button size="icon" variant="outline" className="h-8 w-8" disabled={cur >= pages - 1} onClick={() => setPage(cur + 1)} data-testid="fup-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      <FupUnitForm form={form} setForm={setForm} routers={routers} ifaces={ifaces} loadIfaces={loadIfaces} dataStarlink={dataStarlink} unitCountFor={unitCountFor} busy={busy} onSubmit={save} />
    </div>
  );
}
